import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, MapPin, Quote, Heart, HeartPulse, Clock, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ContactForm from "@/components/site/ContactForm";
import { IMAGES, SERVICES, WHY, VALUES, AREAS, FAQS, PARTNER_VALUES, HOW_STEPS, EVENTS } from "@/lib/data";

const fade = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const SVC_PHYSICIAN = "Physician Practice — Mobile Echocardiography";
const SVC_COMMUNITY = "Community Heart Health / Screening Event";
const SVC_KYH = "Know Your Heart — Individual Screening";
const SVC_GENERAL = "General Inquiry";
const SERVICE_BY_KEY = {
  physician: SVC_PHYSICIAN,
  community: SVC_COMMUNITY,
  kyh: SVC_KYH,
  general: SVC_GENERAL,
};

function Label({ children }) {
  return <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">{children}</span>;
}

export default function HomePage() {
  const now = new Date();
  const upcomingEvents = EVENTS.filter((e) => now <= new Date(e.expiresAt));
  const [presetService, setPresetService] = useState(null);
  const [presetSignal, setPresetSignal] = useState(0);
  const [activeService, setActiveService] = useState(SVC_PHYSICIAN);

  const contactHeading =
    activeService === SVC_COMMUNITY ? "Bring Heart Health to Your Community"
    : activeService === SVC_KYH ? "Request a Know Your Heart Appointment"
    : activeService === SVC_GENERAL ? "Send Us a Message"
    : "Bring Echocardiography to Your Practice";
  const contactText =
    activeService === SVC_COMMUNITY ? "Tell us about your organization and the event you're planning. We'll follow up to discuss your goals, available space, anticipated attendance, and the heart-health services that may be appropriate for your event."
    : activeService === SVC_KYH ? "Tell us your preferred location, date, and time. Our team will follow up to confirm appointment availability."
    : activeService === SVC_GENERAL ? "Have a question or general inquiry? Send us a message and our team will get back to you shortly."
    : "Tell us about your practice and scheduling needs. We'll follow up to coordinate convenient, on-site mobile echocardiography services.";

  const presetForm = (service) => {
    setPresetService(service);
    setPresetSignal((n) => n + 1);
    scrollTo("contact");
  };
  const openCommunityForm = () => presetForm(SVC_COMMUNITY);
  const openPhysicianForm = () => presetForm(SVC_PHYSICIAN);

  // Cross-page preselect via ?service=kyh|community|physician|general
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get("service");
    if (key && SERVICE_BY_KEY[key]) {
      setPresetService(SERVICE_BY_KEY[key]);
      setPresetSignal((n) => n + 1);
      setTimeout(() => scrollTo("contact"), 350);
    }
  }, []);

  return (
    <main className="pt-24">
      {/* HERO */}
      <section id="home" className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center py-16 lg:py-24">
          <motion.div {...fade} className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1.5 mb-7">
              <HeartPulse size={15} className="text-primary" />
              <span className="text-xs font-bold tracking-wide text-secondary">Early Detection · Better Outcomes</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-secondary font-medium leading-[1.05]">
              Advanced Cardiac Imaging. Where You Need It.
            </h1>
            <p className="mt-7 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Cardiac Precision Diagnostics provides professional mobile echocardiography
              services for physician practices and accessible self-pay cardiac screening
              through our Know Your Heart clinic.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="hero-request-button" onClick={openPhysicianForm} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200">
                Request On-Site Service <ArrowRight size={18} />
              </button>
              <button data-testid="hero-services-button" onClick={() => scrollTo("services")} className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-bold text-secondary hover:border-primary hover:text-primary transition-colors duration-200">
                Explore Services
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold text-secondary/70">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> HIPAA-Compliant</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-primary" /> Registered Cardiac Sonographer</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-primary" /> Flexible Scheduling</span>
            </div>
          </motion.div>

          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.15 }} className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-xl">
              <img src={IMAGES.hero} alt="Sonographer performing an echocardiogram" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl border border-border shadow-lg px-6 py-4">
              <p className="font-heading text-3xl text-primary font-semibold">On-Site</p>
              <p className="text-xs tracking-wide uppercase font-bold text-muted-foreground">Echo at your office</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO PATHWAYS */}
      <section id="pathways" className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid md:grid-cols-2 gap-6">
          {/* Physicians */}
          <motion.div {...fade} data-testid="pathway-physicians" className="group rounded-3xl border border-border p-8 lg:p-10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
            <span className="text-xs tracking-[0.22em] uppercase font-bold text-primary">For Physicians</span>
            <h3 className="font-heading text-2xl lg:text-3xl text-secondary mt-3 mb-4">Mobile Echocardiography Services</h3>
            <p className="text-muted-foreground leading-relaxed mb-6 flex-1">
              Bring professional echocardiography services directly into your practice.
              Cardiac Precision Diagnostics partners with physician offices to provide complete
              echocardiograms performed on-site, with professional interpretation by
              board-certified cardiologists.
            </p>
            <button data-testid="pathway-physicians-btn" onClick={() => scrollTo("services")} className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200 w-full sm:w-auto">
              Explore Physician Services <ArrowRight size={18} />
            </button>
          </motion.div>

          {/* Patients */}
          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.1 }} data-testid="pathway-patients" className="group rounded-3xl bg-secondary text-white p-8 lg:p-10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
            <span className="text-xs tracking-[0.22em] uppercase font-bold text-blue-300">For Patients</span>
            <h3 className="font-heading text-2xl lg:text-3xl mt-3 mb-4">Know Your Heart</h3>
            <p className="text-white/80 leading-relaxed mb-5 flex-1">
              Complete echocardiography with professional interpretation by a board-certified
              cardiologist through our scheduled Know Your Heart screening clinics.
            </p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-heading text-4xl font-semibold text-white">$300</span>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300">Self-Pay</span>
            </div>
            <Link data-testid="pathway-patients-btn" to="/know-your-heart" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-secondary hover:bg-blue-100 transition-colors duration-200 w-full sm:w-auto">
              Explore Know Your Heart <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM / VALUE STRIP */}
      <section className="bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid sm:grid-cols-3 gap-10 text-center sm:text-left">
          {[
            { k: "Improve Access", v: "Scan patients on-site — no referrals to outside imaging centers." },
            { k: "Reduce Delays", v: "Eliminate scheduling bottlenecks and long imaging wait times." },
            { k: "Support Decisions", v: "Fast, accurate reports from board-certified cardiologists." },
          ].map((x) => (
            <motion.div {...fade} key={x.k}>
              <p className="font-heading text-2xl mb-2">{x.k}</p>
              <p className="text-white/70 text-sm leading-relaxed">{x.v}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div {...fade} className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden border border-border shadow-lg">
              <img src={IMAGES.consult} alt="Physician consultation" className="w-full h-[440px] object-cover" />
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-7">
            <Label>About Us</Label>
            <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 mb-6 leading-tight">
              Founded with a focus on accessible, high-quality cardiac imaging
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Cardiac Precision Diagnostics, LLC is a mobile diagnostic imaging company
              specializing in high-quality echocardiography delivered directly to physician
              offices, specialty clinics, healthcare facilities, and community organizations
              throughout Texas.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We combine advanced portable ultrasound technology with compassionate,
              patient-centered care — helping practices overcome scheduling delays, limited
              imaging availability, and staffing shortages while improving workflow
              efficiency and patient satisfaction.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-2">Our Mission</p>
                <p className="text-sm text-secondary/80 leading-relaxed">Deliver exceptional mobile cardiovascular diagnostic imaging that improves access, supports providers, and enhances patient outcomes.</p>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-2">Our Vision</p>
                <p className="text-sm text-secondary/80 leading-relaxed">To become Texas' most trusted provider of mobile cardiovascular diagnostic imaging.</p>
              </div>
            </div>
            <div className="mt-9 flex flex-wrap gap-2">
              {VALUES.map((v) => (
                <span key={v} className="rounded-full bg-muted px-4 py-1.5 text-xs font-bold text-secondary">{v}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-muted">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <motion.div {...fade} className="max-w-2xl mb-14">
            <Label>Our Services</Label>
            <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 leading-tight">Comprehensive mobile cardiac imaging, on your terms</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Ico = Icons[s.icon] || HeartPulse;
              return (
                <motion.div
                  {...fade}
                  transition={{ ...fade.transition, delay: i * 0.05 }}
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="group bg-white rounded-2xl border border-border p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                    <Ico size={22} className="text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-xl text-secondary mb-3 leading-snug">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <motion.div {...fade} className="max-w-2xl mb-14">
            <Label>How It Works</Label>
            <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 leading-tight">A simple, seamless process for your practice</h2>
          </motion.div>
          <div className="flex flex-col">
            {HOW_STEPS.map((s, i) => (
              <motion.div
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.06 }}
                key={s.title}
                data-testid={`how-step-${i}`}
                className="flex gap-6"
              >
                <div className="flex flex-col items-center">
                  <div className="h-14 w-14 shrink-0 rounded-full bg-primary text-white flex items-center justify-center font-heading text-xl font-semibold">
                    {i + 1}
                  </div>
                  {i < HOW_STEPS.length - 1 && <div className="w-px flex-1 bg-border my-2" />}
                </div>
                <div className={i < HOW_STEPS.length - 1 ? "pb-10 pt-2" : "pt-2"}>
                  <h3 className="font-heading text-xl lg:text-2xl text-secondary mb-2">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed max-w-2xl">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section id="areas" className="bg-muted">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div {...fade} className="lg:col-span-5">
            <Label>Service Areas</Label>
            <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 mb-6 leading-tight">Serving Greater Houston & surrounding areas</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We bring on-site echocardiography to practices and facilities across the
              region. If you're nearby and don't see your area listed, reach out — we're
              continually expanding coverage.
            </p>
            <button data-testid="areas-contact-button" onClick={openPhysicianForm} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200">
              Check availability in your area <ArrowRight size={16} />
            </button>
          </motion.div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {AREAS.map((a, i) => (
              <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.05 }} key={a.city} className="rounded-2xl border border-border p-7 hover:border-primary transition-colors duration-300">
                <MapPin className="text-primary mb-4" size={24} />
                <h3 className="font-heading text-xl text-secondary mb-2">{a.city}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section id="why" className="bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div {...fade} className="lg:col-span-5">
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-blue-300">Why Choose Us</span>
            <h2 className="font-heading text-3xl lg:text-4xl mt-4 mb-6 leading-tight">Clinical excellence you can rely on</h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Every examination is performed with meticulous attention to quality, patient
              comfort, and efficiency — while maintaining strict HIPAA compliance and
              industry best practices.
            </p>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.fourChamber} alt="Apical four-chamber echocardiogram view on an ultrasound machine" className="w-full h-56 object-cover" />
            </div>
          </motion.div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {WHY.map((w, i) => (
              <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.04 }} key={w} className="flex items-start gap-3">
                <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary flex items-center justify-center"><Check size={14} className="text-white" /></span>
                <span className="text-sm font-medium text-white/90">{w}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY HEART HEALTH OUTREACH */}
      <section id="screenings" className="bg-muted">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          {/* Intro + primary authentic photo */}
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <motion.div {...fade} className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden border border-border shadow-lg">
                <img
                  src={IMAGES.eventEngage1}
                  alt="A Cardiac Precision Diagnostics representative engaging with attendees at a community heart-health outreach event, beside the CPD banner."
                  className="w-full h-[460px] object-cover object-center"
                />
              </div>
            </motion.div>
            <motion.div {...fade} className="lg:col-span-7 order-1 lg:order-2">
              <Label>Heart Health in the Community</Label>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 mb-5 leading-tight">
                Bringing heart-health education and screening directly to your organization
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Cardiac Precision Diagnostics partners with organizations throughout Greater
                Houston to bring accessible heart-health education and screening opportunities
                directly into the communities they serve.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our community outreach events are designed to promote awareness, encourage
                individuals to know their numbers, and connect people with helpful heart-health
                information in an approachable community setting.
              </p>
            </motion.div>
          </div>

          {/* You Provide the Space. We Bring the Echo. — visual demo */}
          <motion.div {...fade} data-testid="community-echo-graphic" className="mt-14">
            <div className="max-w-3xl">
              <h3 className="font-heading text-2xl lg:text-3xl text-secondary leading-tight">
                See Mobile Echo in Action
              </h3>
              <p className="text-muted-foreground leading-relaxed mt-4">
                See how a suitable classroom, meeting room, workplace, or community space can be
                transformed into a private, professional environment for mobile echocardiography.
              </p>
            </div>
            <div className="mt-8 rounded-3xl overflow-hidden border border-border shadow-lg bg-white">
              <img
                src="/community-echo-space.jpg"
                alt="You Provide the Space. We Bring the Echo. — outside and inside view of Cardiac Precision Diagnostics transforming a suitable private room into a professional mobile echocardiography suite with a Philips CX50 ultrasound."
                className="w-full h-auto block"
              />
            </div>
          </motion.div>

          {/* What we bring + Who we partner with */}
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            <motion.div {...fade} data-testid="community-what-we-bring" className="rounded-2xl border border-border bg-white p-8">
              <div className="flex items-center gap-2 mb-5">
                <Icons.HeartHandshake size={20} className="text-primary" />
                <h3 className="font-heading text-xl text-secondary">What we can bring to your event</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Blood Pressure Screening & Know Your Numbers Education",
                  "BE FAST — Stroke Awareness Education",
                  "Know Your Heart™ — Cardiac Ultrasound Screening*",
                  "Heart-Health Education & Resources",
                ].map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check size={13} className="text-primary" />
                    </span>
                    <span className="text-sm text-secondary/90 leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
              <div data-testid="community-ultrasound-note" className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Icons.Heart size={16} className="text-primary" fill="currentColor" />
                  <p className="font-heading text-sm text-secondary">*Cardiac Ultrasound Screening</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Know Your Heart cardiac ultrasound screening may be available as part of select
                  community events when arranged in advance. A suitable private area or room is
                  required to provide participants with an appropriate screening environment.
                  Availability is based on the planned event setup and arrangements with Cardiac
                  Precision Diagnostics.
                </p>
              </div>
            </motion.div>

            <motion.div {...fade} transition={{ ...fade.transition, delay: 0.1 }} data-testid="community-who-we-partner" className="rounded-2xl border border-border bg-white p-8">
              <div className="flex items-center gap-2 mb-5">
                <Icons.Users size={20} className="text-primary" />
                <h3 className="font-heading text-xl text-secondary">Who we partner with</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Employers and businesses",
                  "Churches and faith-based organizations",
                  "School staff & faculty",
                  "Senior communities",
                  "Health fairs",
                  "Wellness programs",
                  "Community organizations",
                ].map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check size={13} className="text-primary" />
                    </span>
                    <span className="text-sm text-secondary/90 leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Bring heart health to your community + secondary photo + CTA */}
          <div className="grid lg:grid-cols-12 gap-14 items-center mt-14">
            <motion.div {...fade} className="lg:col-span-7">
              <h3 className="font-heading text-2xl lg:text-3xl text-secondary mb-5 leading-tight">
                Bring heart health to your community
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Whether you're planning an employee wellness initiative, church health event,
                staff wellness program, senior-community event, or community health fair,
                Cardiac Precision Diagnostics can work with your organization to create a
                professional heart-health outreach experience.
              </p>
              <button
                data-testid="screening-cta-button"
                onClick={openCommunityForm}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200"
              >
                Host a Community Screening Event <ArrowRight size={18} />
              </button>
            </motion.div>
            <motion.div {...fade} transition={{ ...fade.transition, delay: 0.1 }} className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden border border-border shadow-lg">
                <img
                  src={IMAGES.eventBooth}
                  alt="Cardiac Precision Diagnostics professional community event booth setup with informational banner."
                  className="w-full h-[300px] object-cover object-center"
                />
              </div>
            </motion.div>
          </div>

          {/* Cross-navigation to Know Your Heart (informational only) */}
          <motion.div {...fade} data-testid="community-kyh-crossnav" className="mt-14 rounded-3xl bg-secondary text-white p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
            <div className="flex-1">
              <span className="text-xs tracking-[0.22em] uppercase font-bold text-blue-300">Looking for an individual cardiac screening?</span>
              <h3 className="font-heading text-2xl mt-3 mb-3">Know Your Heart</h3>
              <p className="text-white/80 leading-relaxed">
                Know Your Heart is our separate individual self-pay cardiac screening clinic —
                a complete echocardiogram with professional interpretation by a board-certified
                cardiologist for a transparent <span className="font-semibold text-white">$300 self-pay</span> price.
              </p>
            </div>
            <Link
              data-testid="screening-kyh-link"
              to="/know-your-heart"
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-secondary hover:bg-blue-100 transition-colors duration-200"
            >
              Explore Know Your Heart <ArrowRight size={18} />
            </Link>
          </motion.div>

          {/* Program distinction */}
          <motion.div {...fade} data-testid="community-program-distinction" className="mt-8 grid md:grid-cols-[auto,1fr] gap-4 rounded-2xl border border-border bg-white p-7">
            <Icons.GitCompareArrows size={22} className="text-primary shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
              <p className="font-bold text-secondary text-base">Important program distinction</p>
              <p><span className="font-semibold text-secondary">Community Heart Health Outreach</span> is intended for organizations interested in hosting community or group heart-health screening and educational events.</p>
              <p><span className="font-semibold text-secondary">Know Your Heart</span> is the separate pathway for individuals seeking the $300 self-pay cardiac screening clinic.</p>
            </div>
          </motion.div>

          {/* Medical clarity */}
          <div className="mt-6 rounded-xl border border-border bg-white p-5 flex gap-3">
            <ShieldCheck size={20} className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-bold text-secondary">Please note:</span> Community blood-pressure
              screenings and educational activities are intended to promote awareness and early
              detection. They are not diagnostic medical care and do not replace evaluation,
              diagnosis, or treatment by a licensed healthcare provider. Participants with abnormal
              or concerning findings will be encouraged to follow up with their primary care provider
              or a cardiologist for further evaluation.
            </p>
          </div>
        </div>
      </section>

      {/* UPCOMING COMMUNITY EVENTS (temporary — auto-hides past each event's Central-Time date) */}
      {upcomingEvents.length > 0 && (
        <section id="events" className="bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
            <motion.div {...fade} className="max-w-2xl mb-12">
              <Label>Upcoming Community Events</Label>
              <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4 leading-tight">Meet us out in the community</h2>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Come say hello at these upcoming events and learn more about our cardiac
                ultrasound services and community heart-health initiatives.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {upcomingEvents.map((ev, i) => (
                <motion.div
                  {...fade}
                  transition={{ ...fade.transition, delay: i * 0.08 }}
                  key={ev.id}
                  data-testid={`event-card-${ev.id}`}
                  className="relative rounded-3xl border border-primary/25 bg-primary/[0.04] p-8 flex flex-col"
                >
                  <span className="inline-flex items-center gap-2 self-start rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white mb-5">
                    <Icons.Calendar size={13} /> Event
                  </span>
                  <h3 className="font-heading text-xl lg:text-2xl text-secondary mb-3 leading-snug">{ev.title}</h3>
                  <div className="space-y-1.5 mb-4">
                    <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <Icons.CalendarDays size={16} />
                      {ev.date}{ev.time ? ` · ${ev.time}` : ""}
                    </p>
                    {ev.location && (
                      <p className="flex items-center gap-2 text-sm text-secondary/80">
                        <MapPin size={16} className="text-primary" />{ev.location}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{ev.desc}</p>
                  {ev.link && (
                    <a
                      data-testid={`event-details-${ev.id}`}
                      href={ev.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-secondary transition-colors duration-200"
                    >
                      View Event Details <Icons.ExternalLink size={16} />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COMMUNITY AFFILIATION — Fort Bend Regional Partnership (permanent; NOT a medical accreditation) */}
      <section id="affiliation" className="bg-muted border-y border-border">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid md:grid-cols-[auto,1fr] gap-10 lg:gap-14 items-center">
          <motion.div {...fade} className="flex justify-center md:justify-start">
            <img
              src="/fort-bend-proud-member-2026.png"
              alt="Fort Bend Regional Partnership — Proud Member 2026"
              data-testid="fort-bend-badge"
              className="w-44 sm:w-52 h-auto"
            />
          </motion.div>
          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.1 }}>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary">Community Affiliation</span>
            <h2 className="font-heading text-2xl lg:text-3xl text-secondary mt-3 mb-3 leading-tight">Proudly Serving the Fort Bend Community</h2>
            <p className="text-muted-foreground leading-relaxed max-w-xl">
              Cardiac Precision Diagnostics, LLC is a proud 2026 member of the Fort Bend Regional Partnership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHAT PARTNERS VALUE */}
      <section id="testimonials" className="bg-gradient-to-br from-secondary via-secondary to-primary">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <motion.div {...fade} className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-blue-300">What Healthcare Partners Value</span>
            <h2 className="font-heading text-3xl lg:text-4xl text-white mt-4 leading-tight">Built around what matters most to your practice</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {PARTNER_VALUES.map((t, i) => (
              <motion.div {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} key={i} data-testid={`partner-value-${i}`} className="rounded-2xl bg-white/95 backdrop-blur p-8 shadow-xl border border-white/20 hover:-translate-y-1 transition-transform duration-300">
                <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center mb-5">
                  <Heart className="text-white" size={24} fill="currentColor" />
                </div>
                <h3 className="font-heading text-xl text-secondary mb-3">{t.title}</h3>
                <p className="text-secondary/80 leading-relaxed">"{t.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <motion.div {...fade} className="mb-12 text-center">
            <Label>FAQ</Label>
            <h2 className="font-heading text-3xl lg:text-4xl text-secondary mt-4">Frequently asked questions</h2>
          </motion.div>
          <motion.div {...fade}>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="bg-white border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-heading text-lg text-secondary hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
          <motion.div {...fade} className="lg:col-span-5">
            <Label>Request Service</Label>
            <h2 data-testid="contact-heading" className="font-heading text-3xl lg:text-4xl text-secondary mt-4 mb-6 leading-tight">
              {contactHeading}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {contactText}
            </p>
            <div className="space-y-5">
              <a data-testid="contact-phone-link" href="tel:3465384241" className="flex items-center gap-4 group">
                <span className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors duration-300"><Icons.Phone size={20} className="text-primary group-hover:text-white transition-colors duration-300" /></span>
                <span><span className="block text-xs uppercase tracking-wide font-bold text-muted-foreground">Call</span><span className="font-semibold text-secondary">346-538-4241</span></span>
              </a>
              <a data-testid="contact-email-link" href="mailto:info@cardiacprecisiondiagnostics.com" className="flex items-center gap-4 group">
                <span className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors duration-300"><Icons.Mail size={20} className="text-primary group-hover:text-white transition-colors duration-300" /></span>
                <span><span className="block text-xs uppercase tracking-wide font-bold text-muted-foreground">Email</span><span className="font-semibold text-secondary break-all">info@cardiacprecisiondiagnostics.com</span></span>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-location">
                <span className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><MapPin size={20} className="text-primary" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wide font-bold text-muted-foreground">Visit Us</span>
                  <span className="font-semibold text-secondary">Cardiac Precision Diagnostics</span>
                  <span className="block text-xs text-muted-foreground">at MedFlex Spaces</span>
                  <span className="block text-sm text-secondary/80 mt-0.5">7616 Branford Place, Suite 220<br />Sugar Land, TX 77479</span>
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"><Icons.Route size={20} className="text-primary" /></span>
                <span><span className="block text-xs uppercase tracking-wide font-bold text-muted-foreground">Mobile Service Area</span><span className="font-semibold text-secondary">Houston · Katy · Sugar Land · Richmond · Pearland</span></span>
              </div>
              <div data-testid="contact-map" className="mt-2 rounded-2xl overflow-hidden border border-border shadow-sm">
                <iframe
                  title="Cardiac Precision Diagnostics at MedFlex Spaces — 7616 Branford Place, Suite 220, Sugar Land, TX 77479"
                  src="https://www.google.com/maps?q=7616%20Branford%20Place%20Suite%20220%20Sugar%20Land%20TX%2077479&output=embed"
                  className="w-full h-56 block"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>
          <motion.div {...fade} className="lg:col-span-7">
            <ContactForm presetService={presetService} presetSignal={presetSignal} onServiceChange={setActiveService} />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
